import { FormikSelect } from '@/common/presentation/components/forms/fields/FormikSelect';
import { RaptorJobFormValues } from '@/raptorjob/presentation/RaptorJobValidationSchema';
import { Box, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

import { Estimate } from '../../../RaptorJobValidationSchema';
import { FormikTextField } from '@/common/presentation/components/forms/fields/FormikTextField';
import tid from '@/common/application/utils/tid';
import { useFormikContext } from 'formik';
import { RaptorMetricType } from '@/raptorjob/domain/job-metric-type';

interface EstimateRowProps {
  index: number;
}

export const EstimatesRow: React.FC<EstimateRowProps> = ({ index }) => {
  const { values, setFieldValue } = useFormikContext<RaptorJobFormValues>();

  const metricTypeOptions = [
    { id: RaptorMetricType.Hours, name: 'Hours' },
    { id: RaptorMetricType.Count, name: 'Count' },
    { id: RaptorMetricType.Loads, name: 'Loads' },
    { id: RaptorMetricType.Area, name: 'Area' },
    { id: RaptorMetricType.Volume, name: 'Volume' },
    { id: RaptorMetricType.Weight, name: 'Weight' },
    { id: RaptorMetricType.Length, name: 'Length' },
    { id: RaptorMetricType.Other, name: 'Other' }
  ];

  const unitOptions = [
    { id: 'hrs', name: 'Hours' },
    { id: 'days', name: 'Days' },
    { id: 'pcs', name: 'Pieces' },
    { id: 'sqft', name: 'Sq Ft' },
    { id: 'sqm', name: 'Sq M' },
    { id: 'cuft', name: 'Cu Ft' },
    { id: 'cum', name: 'Cu M' },
    { id: 'lbs', name: 'Lbs' },
    { id: 'kg', name: 'Kg' },
    { id: 'ft', name: 'Feet' },
    { id: 'm', name: 'Meters' }
  ];

  const removeEstimate = (index: number) => {
    const currentEstimates = values.estimates || [];
    const updatedEstimates = currentEstimates.filter((_, i) => i !== index);
    setFieldValue('estimates', updatedEstimates);
  };

  const updateEstimate = (index: number, field: keyof Estimate, value: unknown) => {
    const currentEstimates = values.estimates || [];
    const updatedEstimates = [...currentEstimates];
    if (updatedEstimates[index]) {
      updatedEstimates[index] = { ...updatedEstimates[index], [field]: value };
      setFieldValue('estimates', updatedEstimates);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }} {...tid(`jobs:create:estimates-row-${index}`)}>
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          gap: { xs: '0px', sm: '12px' },
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: { xs: 'column', sm: 'row' },
          mb: 1
        }}
      >
        <Box sx={{ flex: 1, width: { xs: '100%', sm: '50%' } }}>
          <FormikSelect
            id={`estimates.${index}.metricType`}
            label="Metric Type"
            options={metricTypeOptions}
            required
            onChange={(e) => updateEstimate(index, 'metricType', e.target.value)}
            {...tid(`jobs:create:estimates-metric-select-${index}`)}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: '12px', width: { xs: '100%', sm: '50%' } }}>
          <Box sx={{ flex: 1 }}>
            <FormikTextField<RaptorJobFormValues>
              id={`estimates.${index}.quantity`}
              label="Quantity"
              type="number"
              required
              onChange={(e) => {
                const value = e.target.value;
                updateEstimate(index, 'quantity', value === '' ? null : Number(value));
              }}
              {...tid(`jobs:create:estimates-quantity-input-${index}`)}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <FormikSelect
              id={`estimates.${index}.unit`}
              label="Unit"
              options={unitOptions}
              required
              onChange={(e) => updateEstimate(index, 'unit', e.target.value)}
              {...tid(`jobs:create:estimates-unit-select-${index}`)}
            />
          </Box>
        </Box>
      </Box>
      <Box sx={{ mb: 2 }}>
        <IconButton
          onClick={() => removeEstimate(index)}
          sx={{ color: 'error.main' }}
          aria-label="Remove estimate"
          {...tid(`jobs:create:remove-estimate-button-${index}`)}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    </Box>
  );
};
